// Sdílený shell modalů šablony — desktop centrovaný dialog, na mobilu (< 1024)
// bottom sheet se swipe-down. Vytažený ze shippingModal.js 2026-09-02, když
// přibyl druhý modal téhož druhu (servis, serviceModal.js); modal košíku
// (cartModal.js) zatím jede na vlastní kopii chování — sjednotit až se bude
// sahat na něj (má navíc sticky patičku a stav košíku v hlavičce).
//
// Shell staví RÁM (scrim · panel · grabber · hlavička s medailonkem, titulkem,
// podtitulkem a ✕ · tělo · patička s odkazem a tlačítkem) a obsluhuje CHOVÁNÍ:
// scroll lock, ESC (capture na dokumentu), focus trap, návrat fokusu na opener,
// swipe-down za grabber i hlavičku, Android zpět přes history state, reduced
// motion. Obsah těla plní volající; shell o něm nic neví.
//
// Třídy: každý prvek nese sdílenou `bs-modal__x` (styl rámu,
// components/_modal-shell.scss) a instanční `<prefix>__x` (styl obsahu
// a devchecky) — DOM modalu dopravy se tím proti stavu před vytažením nemění,
// jen ke každé třídě přibyla sdílená. Totéž platí pro zámek scrollu na `body`
// (`bs-modal-open` + `<prefix>-open`).

import { icon } from './icon.js';

const MOBILE_MQ = '(max-width: 1023px)';
const FOCUSABLE = 'button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])';
// pojistka, kdyby transitionend nedoběhl (např. panel bez transition)
const REMOVE_FALLBACK_MS = 450;
/** Pojistka navigace z odkazu, když po history.back() nepřijde popstate. */
const LEAVE_FALLBACK_MS = 600;
// hranice swipe-down pro zavření sheetu (podíl výšky panelu)
const SWIPE_CLOSE_RATIO = 0.3;

export function shellNode(tag, className, content) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (content != null) el.textContent = content;
  return el;
}

/** `bs-modal__name prefix__name` — sdílená + instanční třída prvku rámu. */
export function shellClass(prefix, name) {
  return 'bs-modal__' + name + ' ' + prefix + '__' + name;
}

function isMobile() {
  return window.matchMedia && window.matchMedia(MOBILE_MQ).matches;
}

function reducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Továrna na jeden modal. Volající drží výsledek jako singleton modulu
 * a plní `ui.body` po `open()`.
 *
 * @param {object} opts
 * @param {string} opts.prefix      instanční BEM prefix (`bs-ship`, `bs-serv`)
 * @param {string} opts.icon        symbol spritu do medailonku hlavičky
 * @param {string} opts.title       titulek (a `aria-label` dialogu)
 * @param {string} [opts.sub]       podtitulek pod titulkem
 * @param {string} opts.closeLabel  `aria-label` tlačítka ✕
 * @param {string} opts.doneLabel   text tlačítka patičky
 * @param {{label: string, href: string|function}} [opts.link]
 *                                  odkaz vlevo v patičce; `href` jako funkce
 *                                  se vyhodnotí při otevření (čte navigaci)
 * @param {function} [opts.onClose] volá se po úklidu (intervaly volajícího)
 */
export function createModal(opts) {
  const prefix = opts.prefix;
  let st = null;

  function build() {
    const root = shellNode('div', 'bs-modal ' + prefix);
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', opts.title);

    const scrim = shellNode('div', shellClass(prefix, 'scrim'));
    scrim.addEventListener('click', close);
    root.appendChild(scrim);

    const panel = shellNode('div', shellClass(prefix, 'panel'));
    root.appendChild(panel);

    const grab = shellNode('div', shellClass(prefix, 'grab'));
    grab.appendChild(shellNode('span', shellClass(prefix, 'grabber')));
    panel.appendChild(grab);

    const head = shellNode('header', shellClass(prefix, 'head'));
    const badge = shellNode('span', shellClass(prefix, 'badge'));
    badge.appendChild(icon(opts.icon));
    head.appendChild(badge);
    const headTxt = shellNode('div', shellClass(prefix, 'headtxt'));
    headTxt.appendChild(shellNode('strong', shellClass(prefix, 'title'), opts.title));
    if (opts.sub) headTxt.appendChild(shellNode('span', shellClass(prefix, 'sub'), opts.sub));
    head.appendChild(headTxt);
    const closeBtn = shellNode('button', shellClass(prefix, 'close'));
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', opts.closeLabel);
    closeBtn.appendChild(icon('close'));
    closeBtn.addEventListener('click', close);
    head.appendChild(closeBtn);
    panel.appendChild(head);

    const body = shellNode('div', shellClass(prefix, 'body'));
    panel.appendChild(body);

    const foot = shellNode('footer', shellClass(prefix, 'foot'));
    let link = null;
    if (opts.link) {
      link = shellNode('a', shellClass(prefix, 'link'), opts.link.label);
      link.href = typeof opts.link.href === 'function' ? opts.link.href() : opts.link.href;
      foot.appendChild(link);
    }
    const done = shellNode('button', shellClass(prefix, 'done'), opts.doneLabel);
    done.type = 'button';
    done.addEventListener('click', close);
    foot.appendChild(done);
    panel.appendChild(foot);

    return { root: root, panel: panel, grab: grab, head: head, body: body, close: closeBtn, foot: foot, link: link, done: done };
  }

  // --- fokus, klávesnice, gesta --------------------------------------------

  function onKey(e) {
    if (!st) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = [...st.ui.root.querySelectorAll(FOCUSABLE)].filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Swipe-down za grabber i hlavičku (tělo scrolluje nezávisle).
  function bindSwipe(ui) {
    let startY = null;
    let dy = 0;
    const onMove = function (e) {
      if (startY == null) return;
      dy = Math.max(0, e.clientY - startY);
      ui.panel.style.transform = 'translateY(' + dy + 'px)';
      ui.panel.style.transition = 'none';
    };
    const onUp = function () {
      if (startY == null) return;
      const h = ui.panel.getBoundingClientRect().height;
      ui.panel.style.transition = '';
      startY = null;
      if (dy > h * SWIPE_CLOSE_RATIO) {
        close();
      } else {
        ui.panel.style.transform = '';
      }
      dy = 0;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    const onDown = function (e) {
      if (!isMobile() || !e.isPrimary) return;
      startY = e.clientY;
      dy = 0;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };
    ui.grab.addEventListener('pointerdown', onDown);
    ui.head.addEventListener('pointerdown', function (e) {
      if (e.target instanceof Element && e.target.closest('button, a')) return;
      onDown(e);
    });
  }

  function onPop() {
    if (!st) return;
    const leaveTo = st.leaveTo;
    st.histPushed = false;
    destroy();
    if (leaveTo) window.location.assign(leaveTo);
  }

  // Odkaz z modalu ve stejném okně: pushnutý stav historie by na cílové stránce
  // zůstal jako fantomový krok Zpět (#316, B2-05). Nejdřív ho shodit
  // (history.back), navigovat až z popstate.
  // Mezi history.back() a popstate modal čeká na navigaci (st.leaveTo): další
  // klik na odkaz se zahodí a zavření (×, ESC, swipe) nic neudělá, jinak by
  // destroy() odebral onPop a cíl by se ztratil. Nepřijde-li popstate vůbec,
  // naviguje pojistka po LEAVE_FALLBACK_MS sama.
  function onLinkClick(e) {
    if (st && st.leaveTo) {
      if (e.target instanceof Element && e.target.closest('a[href]')) e.preventDefault();
      return;
    }
    if (!st || !st.histPushed || e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
    const url = new URL(a.href, window.location.href);
    if (!/^https?:$/.test(url.protocol)) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;
    e.preventDefault();
    st.leaveTo = url.href;
    st.histPushed = false;
    const pending = st;
    setTimeout(function () {
      if (st !== pending) return; // popstate už navigoval
      destroy();
      window.location.assign(pending.leaveTo);
    }, LEAVE_FALLBACK_MS);
    window.history.back();
  }

  // --- otevření / zavření ---------------------------------------------------

  /** Otevře modal a vrátí jeho uzly; `null`, když už je otevřený. */
  function open() {
    if (st) return null;
    const ui = build();
    st = {
      ui: ui,
      histPushed: false,
      leaveTo: null,
      opener: document.activeElement instanceof HTMLElement ? document.activeElement : null,
    };
    ui.root.addEventListener('click', onLinkClick);
    document.body.appendChild(ui.root);
    document.body.classList.add('bs-modal-open', prefix + '-open');
    document.addEventListener('keydown', onKey, true);
    bindSwipe(ui);

    if (isMobile()) {
      try {
        window.history.pushState({ bsModal: prefix }, '');
        st.histPushed = true;
      } catch (e) {
        /* file:// fixtures */
      }
    }
    window.addEventListener('popstate', onPop);

    if (reducedMotion()) {
      ui.root.classList.add('is-open');
    } else {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (st) st.ui.root.classList.add('is-open');
        });
      });
    }
    ui.close.focus();
    return ui;
  }

  function close() {
    if (!st || st.leaveTo) return; // čeká se na navigaci z odkazu
    if (st.histPushed) {
      st.histPushed = false;
      window.history.back(); // popstate dokončí úklid
    } else {
      destroy();
    }
  }

  function destroy() {
    if (!st) return;
    const s = st;
    st = null;
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('popstate', onPop);
    document.body.classList.remove('bs-modal-open', prefix + '-open');
    const remove = function () {
      s.ui.root.remove();
    };
    if (reducedMotion()) {
      remove();
    } else {
      s.ui.root.classList.remove('is-open');
      s.ui.root.addEventListener('transitionend', remove, { once: true });
      setTimeout(remove, REMOVE_FALLBACK_MS);
    }
    if (s.opener && document.contains(s.opener)) s.opener.focus();
    if (opts.onClose) opts.onClose();
  }

  return {
    open: open,
    close: close,
    isOpen: function () {
      return !!st;
    },
  };
}
